import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';

const trapFocusHandlers = {};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    const summaryElements = this.querySelectorAll('summary');
    this.addAccessibilityAttributes(summaryElements);

    if (navigator.platform === 'iPhone')
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${window.innerHeight}px`
      );

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    const summaries = this.querySelectorAll('summary');
    const buttons = this.querySelectorAll('.open-menu-drawer');

    summaries.forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    buttons.forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  addAccessibilityAttributes(summaryElements) {
    summaryElements.forEach((element) => {
      element.setAttribute('role', 'button');
      element.setAttribute('aria-expanded', false);
      element.setAttribute('aria-controls', element.nextElementSibling?.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector('summary')
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 1023px)').matches) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        summaryElement.setAttribute('aria-expanded', true);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    summaryElement.setAttribute('aria-expanded', true);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (!event) return;

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      elementToFocus?.setAttribute('aria-expanded', false);
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (!event) return;

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      elementToFocus?.setAttribute('aria-expanded', false);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      ) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(
            detailsElement.closest('details[open]'),
            detailsElement.querySelector('summary')
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

const initSwipers = (menuElement) => {
  const menus = menuElement
    ? [menuElement]
    : document.querySelectorAll('.jc-menu-drawer');

  if (!menus.length) {
    console.log('Menu not found');
    return;
  }

  menus.forEach((menu) => {
    if (menu.hidden || menu.closest('[hidden]')) return;

    const swipers = menu.querySelectorAll('.jc-swiper');

    swipers.forEach((swiperElement) => {
      if (swiperElement.closest('[hidden]')) return;

      if (swiperElement.swiper) {
        swiperElement.swiper.update();
        return;
      }

      const swiperPagination =
        menu.querySelector('.bestsellers__pagination') || null;

      new Swiper(swiperElement, {
        modules: [Pagination],
        spaceBetween: 16,
        slidesPerView: 'auto',
        pagination: {
          el: swiperPagination,
          clickable: true
        }
      });
    });
  });
};

const mobileMenuDrawers = new Set();
let newMenuValue = window.newMenu;
let isNewMenuPropertyBound = false;

const applyMobileMenuVariant = (drawer, value = window.newMenu) => {
  const variants = drawer.querySelectorAll('[data-mobile-menu-variant]');
  const useNewMenu = value === true;

  if (!variants.length) return;

  drawer.setAttribute('data-new-menu', useNewMenu ? 'true' : 'false');

  variants.forEach((variant) => {
    const isActiveVariant =
      variant.getAttribute('data-mobile-menu-variant') ===
      (useNewMenu ? 'new' : 'old');

    variant.hidden = !isActiveVariant;

    if (isActiveVariant) {
      requestAnimationFrame(() => initSwipers(variant));
    }
  });
};

const applyMobileMenuVariants = () => {
  mobileMenuDrawers.forEach((drawer) => applyMobileMenuVariant(drawer));
};

const bindNewMenuProperty = () => {
  if (isNewMenuPropertyBound) return;

  const descriptor = Object.getOwnPropertyDescriptor(window, 'newMenu');

  if (descriptor && !descriptor.configurable) {
    isNewMenuPropertyBound = true;
    return;
  }

  const originalGetter = descriptor?.get;
  const originalSetter = descriptor?.set;

  newMenuValue = originalGetter ? originalGetter.call(window) : window.newMenu;

  Object.defineProperty(window, 'newMenu', {
    configurable: true,
    get() {
      return newMenuValue;
    },
    set(value) {
      newMenuValue = value;
      originalSetter?.call(window, value);
      applyMobileMenuVariants();
    }
  });

  isNewMenuPropertyBound = true;
};

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.menuDrawer = this.querySelector('.js-menu-mobile summary');
    this.header = document.querySelector('header');
    this.searchBar = document.getElementById('jolly-search-bar');
    this.borderOffset = 0;

    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    }

    // Bind scroll handler
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll);
    mobileMenuDrawers.add(this);
    bindNewMenuProperty();
    applyMobileMenuVariant(this);
    initSwipers();
  }

  openMenuDrawer(summaryElement) {
    if (!this.header) return;

    this.borderOffset = 0;

    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );

    this.header.classList.add('menu-open');
    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    document.body.style.overflow = 'hidden';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'menu_open' });
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    this.menuDrawer.setAttribute('aria-expanded', false);
    window.removeEventListener('resize', this.onResize);
    document.body.style.overflow = '';
  }

  debounce(callback, delay) {
    let timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(callback, delay);
    };
  }

  onResize = this.debounce(() => {
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    }
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  }, 200);

  handleScroll() {
    if (!this.header) return;
    if (window.scrollY > 200) {
      this.header.classList.add('header--shadow');
    } else if (window.scrollY < 150) {
      this.header.classList.remove('header--shadow');
    }
  }
}

customElements.define('header-drawer', HeaderDrawer);
